import { useState } from "react";
import Reveal from "@/components/Reveal";
import { Monitor, Laptop, HardDrive, Wifi, Shield, Smartphone, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const services = [
  {
    icon: Laptop,
    title: "Laptop Repair",
    desc: "Screen replacements, battery swaps, and hardware fixes for all brands.",
    details: {
      overview:
        "Whether your laptop won't turn on, has a cracked screen, or is running slow, our certified technicians handle repairs for all major brands including Dell, HP, Lenovo, ASUS, Apple, and more.",
      includes: [
        "Cracked or dim screen replacement",
        "Battery replacement and power issues",
        "Keyboard, trackpad, and hinge repair",
        "Liquid damage assessment and cleanup",
        "SSD / RAM upgrades for faster performance",
        "Overheating and fan replacement",
      ],
      turnaround: "Most repairs completed in 1–3 business days.",
    },
  },
  {
    icon: Monitor,
    title: "Desktop Repair",
    desc: "Custom builds, upgrades, and troubleshooting for PC and Mac systems.",
    details: {
      overview:
        "From diagnosing a stubborn boot loop to designing a high-performance custom PC, we service and build desktops for home users, gamers, and businesses across East Cobb.",
      includes: [
        "Custom PC builds tailored to your needs",
        "Motherboard, PSU, and GPU replacement",
        "Windows and macOS reinstall & tuning",
        "Storage upgrades and cloning",
        "Cooling and airflow optimization",
        "Diagnostics for random crashes or blue screens",
      ],
      turnaround: "Diagnostics same-day; most repairs within 2–4 business days.",
    },
  },
  {
    icon: HardDrive,
    title: "Data Recovery",
    desc: "Recover lost files from crashed drives, SSDs, and corrupted storage.",
    details: {
      overview:
        "Lost precious photos, business files, or an entire drive? Our recovery specialists work on failed HDDs, SSDs, USB drives, and memory cards with a no-recovery, no-fee guarantee on most cases.",
      includes: [
        "Failed or clicking hard drives",
        "Corrupted or formatted SSDs",
        "Accidental deletions and partition loss",
        "Ransomware / virus recovery",
        "External drives, USB sticks, SD cards",
        "Secure data transfer to new device",
      ],
      turnaround: "Free evaluation; standard recovery in 3–7 business days.",
    },
  },
  {
    icon: Wifi,
    title: "Network Setup",
    desc: "Home and office Wi-Fi configuration, security, and optimization.",
    details: {
      overview:
        "We design and install reliable, secure networks — from a stronger home Wi-Fi to full small-business setups with mesh coverage, guest networks, and hardware firewalls.",
      includes: [
        "Router, mesh, and access point installation",
        "Wired ethernet runs and switches",
        "Wi-Fi coverage and speed optimization",
        "Guest networks and parental controls",
        "Business-grade firewalls and VPN",
        "Printer and smart device connectivity",
      ],
      turnaround: "On-site consultations typically same or next-day.",
    },
  },
  {
    icon: Shield,
    title: "Virus Removal",
    desc: "Malware, spyware, and ransomware removal with security hardening.",
    details: {
      overview:
        "If your computer is popping up warnings, running slow, or has been hit by ransomware, we remove the threat, recover what we can, and lock the system down so it doesn't happen again.",
      includes: [
        "Full malware, spyware, and adware removal",
        "Ransomware decryption assessment",
        "Browser hijacker and pop-up cleanup",
        "Antivirus install and configuration",
        "Windows / macOS security hardening",
        "Post-cleanup performance tune-up",
      ],
      turnaround: "Most cleanups completed within 1–2 business days.",
    },
  },
  {
    icon: Smartphone,
    title: "Remote Support",
    desc: "Take us with you anywhere. Expert help with just a click.",
    details: {
      overview:
        "Skip the drive — connect with a technician from your home or office. We securely take control of your screen to fix software issues, install programs, and answer questions in real time.",
      includes: [
        "Software installs and updates",
        "Email, printer, and account setup",
        "Slow computer tune-ups",
        "Basic troubleshooting and how-to help",
        "Business remote IT support plans",
        "Secure, one-time or scheduled sessions",
      ],
      turnaround: "Same-day sessions available during business hours.",
    },
  },
];

const ServicesSection = () => {
  const [active, setActive] = useState<number | null>(null);
  const current = active !== null ? services[active] : null;

  return (
    <section id="services" className="py-20 bg-background">
      <div className="container">
        <div className="text-center mb-14">
          <p className="text-primary font-semibold text-sm uppercase tracking-widest mb-3">Discover</p>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-foreground">
            What We Do
          </h2>
          <p className="text-sm text-muted-foreground mt-3">Tap any service for details.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08} className="h-full">
              <button
                type="button"
                onClick={() => setActive(i)}
                className="card-service h-full w-full text-left cursor-pointer hover:-translate-y-1 hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <s.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-display font-bold text-lg text-primary mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">{s.desc}</p>
                <span className="inline-flex items-center text-xs font-semibold text-primary">
                  Learn more <ArrowRight className="ml-1 h-3 w-3" />
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={active !== null} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent className="max-w-lg">
          {current && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center">
                    <current.icon className="h-5 w-5 text-primary" />
                  </div>
                  <DialogTitle className="font-display text-2xl">{current.title}</DialogTitle>
                </div>
                <DialogDescription className="text-base leading-relaxed text-muted-foreground">
                  {current.details.overview}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-2">
                <h4 className="font-semibold text-sm uppercase tracking-wide text-primary mb-2">
                  What's included
                </h4>
                <ul className="space-y-1.5 text-sm text-foreground">
                  {current.details.includes.map((item) => (
                    <li key={item} className="flex gap-2">
                      <span className="text-primary font-bold">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-4 rounded-lg bg-muted/50 p-3 text-sm">
                <span className="font-semibold text-foreground">Typical turnaround: </span>
                <span className="text-muted-foreground">{current.details.turnaround}</span>
              </div>

              <a href="#booking" onClick={() => setActive(null)}>
                <Button className="w-full mt-2">Book this service</Button>
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ServicesSection;
